import React, { useRef } from "react";
import LocalFilterPanel from "../../../components/common/LocalFilterPanel";
import { useOcelMetadata } from "../../../stores/OcelContext";
import {
  ComparisonSection,
  BetrachtungszeitraumSection,
} from "../_shared/PeriodSettingsControls";

// ── Ampel color picker — duplicated from EfficiencyKPISettings.jsx (not
// exported there) since the two tiles' Ampel sections otherwise diverge only
// in label wording (percent vs. plain count thresholds). ────────────────────
const PALETTE = [
  { hex: '#22c55e', label: 'Green' },
  { hex: '#eab308', label: 'Yellow' },
  { hex: '#ef4444', label: 'Red' },
  { hex: '#f97316', label: 'Orange' },
  { hex: '#6366f1', label: 'Default' },
];

const ColorPicker = ({ value, onChange, label, disabled }) => {
  const inputRef = useRef(null);
  const isCustom = !PALETTE.some((p) => p.hex === value);
  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>}
      <div className="flex items-center gap-1.5 flex-wrap">
        {PALETTE.map(({ hex, label: l }) => (
          <button key={hex} onClick={() => onChange(hex)} title={l} type="button" disabled={disabled}
            className={`w-7 h-7 rounded-full transition-all cursor-pointer ${value === hex ? 'ring-2 ring-offset-2 ring-gray-600 scale-110' : 'opacity-75 hover:opacity-100 hover:scale-110'} disabled:opacity-40 disabled:cursor-not-allowed`}
            style={{ backgroundColor: hex }} />
        ))}
        {isCustom && (
          <button onClick={() => inputRef.current?.click()} title={value} type="button" disabled={disabled}
            className="w-7 h-7 rounded-full ring-2 ring-offset-2 ring-gray-600 scale-110 cursor-pointer"
            style={{ backgroundColor: value }} />
        )}
        <div className="relative w-7 h-7">
          <input ref={inputRef} type="color" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" tabIndex={-1} />
          <div className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center text-gray-400 pointer-events-none text-[10px]">
            ✎
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Ampel (traffic light) configuration — thresholds are plain object
// counts here, not percentages, so the labels drop the "(%)" suffix that
// EfficiencyKPISettings.jsx uses; the 3-zone comparison logic itself
// (resolveAmpelColor) is dimension-agnostic and needs no change. ───────────
const AmpelSection = ({
  enabled, lower, upper, colorRed, colorYellow, colorGreen,
  onEnabledChange, onLowerChange, onUpperChange, onColorRedChange, onColorYellowChange, onColorGreenChange,
  loading,
}) => {
  const [lowerInput, setLowerInput] = React.useState(String(lower ?? 0));
  const [upperInput, setUpperInput] = React.useState(String(upper ?? 100));

  const lowerNum = parseFloat(lowerInput);
  const upperNum = parseFloat(upperInput);
  const rangeError = enabled && !isNaN(lowerNum) && !isNaN(upperNum) && lowerNum >= upperNum
    ? 'Unterer Schwellenwert muss kleiner als der obere sein.' : null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <label className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" disabled={loading} />
        <span className="text-xs font-semibold text-gray-700">Ampel aktivieren</span>
      </label>
      {enabled && (
        <div className="px-3 py-3 space-y-3 bg-white">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unterer Schwellenwert</label>
              <input type="number" step="any" value={lowerInput}
                onChange={(e) => { setLowerInput(e.target.value); const p = parseFloat(e.target.value); if (!isNaN(p)) onLowerChange(p); }}
                onBlur={() => { const p = parseFloat(lowerInput); setLowerInput(String(isNaN(p) ? 0 : p)); onLowerChange(isNaN(p) ? 0 : p); }}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${rangeError ? 'border-red-400' : 'border-gray-300'}`} disabled={loading} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Oberer Schwellenwert</label>
              <input type="number" step="any" value={upperInput}
                onChange={(e) => { setUpperInput(e.target.value); const p = parseFloat(e.target.value); if (!isNaN(p)) onUpperChange(p); }}
                onBlur={() => { const p = parseFloat(upperInput); setUpperInput(String(isNaN(p) ? 0 : p)); onUpperChange(isNaN(p) ? 0 : p); }}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${rangeError ? 'border-red-400' : 'border-gray-300'}`} disabled={loading} />
            </div>
          </div>
          {rangeError && <p className="text-xs text-red-500">{rangeError}</p>}
          <div className="grid grid-cols-3 gap-3">
            <ColorPicker value={colorRed} onChange={onColorRedChange} label="Rot (< unterer)" disabled={loading} />
            <ColorPicker value={colorYellow} onChange={onColorYellowChange} label="Gelb (dazwischen)" disabled={loading} />
            <ColorPicker value={colorGreen} onChange={onColorGreenChange} label="Grün (>= oberer)" disabled={loading} />
          </div>
        </div>
      )}
    </div>
  );
};

const ObjectCountKPISettings = ({
  componentTitle,
  onTitleChange,
  objectType,
  onObjectTypeChange,
  ampelEnabled,
  ampelLower,
  ampelUpper,
  ampelColorRed,
  ampelColorYellow,
  ampelColorGreen,
  onAmpelEnabledChange,
  onAmpelLowerChange,
  onAmpelUpperChange,
  onAmpelColorRedChange,
  onAmpelColorYellowChange,
  onAmpelColorGreenChange,
  availableObjectTypes,
  localFilters,
  updateLocalFilter,
  activeFile,
  loading,
  // Betrachtungszeitraum / Vorperioden-Vergleich
  periodPreset,
  onPeriodPresetChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  showPeriodLabel,
  onShowPeriodLabelChange,
  comparisonEnabled,
  onComparisonEnabledChange,
  trendDirection,
  onTrendDirectionChange,
}) => {
  const metadata = useOcelMetadata(activeFile);
  const datasetMin = metadata?.date_range?.start ? metadata.date_range.start.split("T")[0] : undefined;
  const datasetMax = metadata?.date_range?.end ? metadata.date_range.end.split("T")[0] : undefined;

  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    updateLocalFilter("objectTypes", val ? [val] : []);
  };

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Component Title <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Object Count KPI"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Object Type</label>
        <select
          value={objectType}
          onChange={(e) => handleObjectTypeChange(e.target.value)}
          disabled={loading || availableObjectTypes.length === 0}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">All object types</option>
          {availableObjectTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Objekte dieses Typs werden gezählt</p>
      </div>

      {/* Betrachtungszeitraum */}
      <BetrachtungszeitraumSection
        periodPreset={periodPreset}
        onPeriodPresetChange={onPeriodPresetChange}
        customStart={customStart}
        onCustomStartChange={onCustomStartChange}
        customEnd={customEnd}
        onCustomEndChange={onCustomEndChange}
        datasetMin={datasetMin}
        datasetMax={datasetMax}
        comparisonEnabled={comparisonEnabled}
      />

      {/* Periodenbezeichnung anzeigen */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showPeriodLabel}
          onChange={(e) => onShowPeriodLabelChange(e.target.checked)}
          disabled={loading}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Periodenbezeichnung anzeigen</span>
      </label>

      {/* Ampel — unabhängig vom Vorperioden-Vergleich */}
      <AmpelSection
        enabled={ampelEnabled}
        lower={ampelLower}
        upper={ampelUpper}
        colorRed={ampelColorRed}
        colorYellow={ampelColorYellow}
        colorGreen={ampelColorGreen}
        onEnabledChange={onAmpelEnabledChange}
        onLowerChange={onAmpelLowerChange}
        onUpperChange={onAmpelUpperChange}
        onColorRedChange={onAmpelColorRedChange}
        onColorYellowChange={onAmpelColorYellowChange}
        onColorGreenChange={onAmpelColorGreenChange}
        loading={loading}
      />

      {/* Vorperioden-Vergleich — unabhängig von der Ampel */}
      <ComparisonSection
        periodPreset={periodPreset}
        comparisonEnabled={comparisonEnabled}
        onComparisonEnabledChange={onComparisonEnabledChange}
        trendDirection={trendDirection}
        onTrendDirectionChange={onTrendDirectionChange}
        loading={loading}
        radioGroupName="objectCountKpiTrendDirection"
      />

      {/* LocalFilterPanel — "activityNodes" + "objectAttributes" aktiviert.
          "dateRange" bleibt ausgeschlossen: der Betrachtungszeitraum oben
          deckt Zeitraum-Scoping bereits (reichhaltiger, mit Presets/Vergleich)
          ab, ein zweiter Datums-Regler wäre redundant/widersprüchlich.
          "activityNodes" ersetzt den früheren, ODER-only "Aktivitätsfilter"-
          Block (ActivityFilterBlock): der schrieb intern immer das Legacy-
          included/excluded-Format, das im Backend (filter/engine.py,
          _apply_activity_filter) hart auf match_type='any' gezwungen wird.
          Der activityNodes-Filter (ActivityNodeFilterControl) bringt dagegen
          eine fertige "Match any/all"-UI mit und wird backend-seitig über
          activity_node_filters/_apply_activity_node_filter korrekt als
          ODER/UND ausgewertet — deshalb kein separater Aktivitätsfilter-
          Abschnitt mehr nötig. */}
      <div className="border-t border-gray-100 pt-4">
        <LocalFilterPanel
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
          disabled={loading}
          enabledFilterTypes={['activityNodes', 'objectAttributes']}
        />
      </div>
    </div>
  );
};

export default ObjectCountKPISettings;
